import { useEffect, useRef } from 'react';

import { FamilyTreeWarningType } from '@/types/schema';

import { useFamilyTreeContext } from '../contexts';

const VISITED_STORAGE_KEY_PREFIX = 'familyTree:visited:';

/**
 * Auto-opens the unlinked individuals side panel on the user's first visit
 * to the family tree tab for a given household. On subsequent visits within
 * the same browser session, the panel remains closed by default.
 */
export function useAutoOpenUnlinkedPanel() {
  const { state, dispatch } = useFamilyTreeContext();
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    // Only run once per mount, skip in presentation mode
    if (hasAutoOpened.current || state.presentationMode) return;

    // Wait until family tree data is loaded
    if (!state.familyTreeData) return;

    // Ensure this effect only runs once after data is available
    hasAutoOpened.current = true;

    const storageKey = `${VISITED_STORAGE_KEY_PREFIX}${state.householdId}`;
    const hasVisitedBefore = sessionStorage.getItem(storageKey) === 'true';

    // Mark as visited for this session
    sessionStorage.setItem(storageKey, 'true');

    if (hasVisitedBefore) return;

    // Check if there are unlinked family members
    const hasUnlinkedMembers =
      state.familyTreeData.warnings?.some(
        (warning) => warning.type === FamilyTreeWarningType.UnlinkedFamilyMember
      ) ?? false;

    if (hasUnlinkedMembers) {
      dispatch({
        type: 'OPEN_SIDE_PANEL',
        personId: '',
        variant: 'unlinked',
      });
    }
  }, [
    dispatch,
    state.familyTreeData,
    state.householdId,
    state.presentationMode,
  ]);
}
